import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import {
  Alert,
  DataTable,
  Empty,
  Kpi,
  KpiGrid,
  Muted,
  PageHeader,
  Section,
  StatusBadge,
} from '@/components/ui'
import { fetchRescisoes } from './api'
import { RescisaoWizard } from './RescisaoWizard'
import { formatDate, formatMoney } from './utils'

function tone(status?: string) {
  const s = (status || '').toLowerCase()
  if (/(rascunho|calculad|abert|pend)/.test(s)) return 'pendente' as const
  if (/(cancel|rejeit)/.test(s)) return 'inativo' as const
  return 'ativo' as const
}

type Mode = 'lista' | 'nova'

export function RescisaoPage() {
  const { canEdit } = useAuth()
  const editable = canEdit('dp-rescisao')
  const qc = useQueryClient()
  const [params, setParams] = useSearchParams()
  const mode: Mode = params.get('nova') === '1' ? 'nova' : 'lista'
  const [msg, setMsg] = useState('')
  const [msgTone, setMsgTone] = useState<'success' | 'info'>('success')

  const lista = useQuery({
    queryKey: ['dp', 'rescisoes'],
    queryFn: fetchRescisoes,
  })

  const dados = lista.data?.dados || []
  const abertas = dados.filter((r) =>
    /rascunho|calculad|abert|pend/i.test(r.status || ''),
  ).length

  function openNova() {
    setParams({ nova: '1' })
  }

  function closeNova() {
    setParams({})
  }

  if (mode === 'nova') {
    return (
      <section className="hi-page">
        <PageHeader
          backTo={{ to: '/app/dp/rescisao', label: 'Rescisão' }}
          title="Nova rescisão"
          subtitle="Wizard de 7 etapas — tipo, aviso, pendências, férias, benefícios, conferência e documentos."
        />
        {msg ? <Alert tone={msgTone}>{msg}</Alert> : null}
        <RescisaoWizard
          onCancel={closeNova}
          onSuccess={async () => {
            setMsgTone('success')
            setMsg('Rescisão registrada com sucesso.')
            closeNova()
            await qc.invalidateQueries({ queryKey: ['dp', 'rescisoes'] })
          }}
        />
      </section>
    )
  }

  return (
    <section className="hi-page">
      <PageHeader
        title="Rescisão"
        subtitle="Processos de desligamento — wizard CLT na SPA (ideias do legado)."
        actions={
          <>
            <a className="btn btn-secondary" href={legacyAppUrl('#/dp-rescisao')}>
              Legado
            </a>
            {editable ? (
              <button type="button" className="btn btn-primary" onClick={openNova}>
                Nova rescisão
              </button>
            ) : null}
          </>
        }
      />

      {msg ? <Alert tone={msgTone}>{msg}</Alert> : null}

      <KpiGrid>
        <Kpi label="Processos" value={String(lista.data?.meta?.total ?? dados.length)} />
        <Kpi label="Em aberto" value={String(abertas)} />
      </KpiGrid>

      <Section title="Rescisões" flush>
        {lista.isLoading ? <Muted>Carregando…</Muted> : null}
        {lista.isError ? (
          <Alert tone="danger">
            {lista.error instanceof ApiError ? lista.error.message : 'Falha ao listar.'}
          </Alert>
        ) : null}
        {!lista.isLoading && dados.length === 0 ? (
          <Empty>
            Nenhuma rescisão registrada.
            {editable ? ' Use “Nova rescisão” para iniciar o wizard.' : null}
          </Empty>
        ) : null}
        {dados.length > 0 ? (
          <DataTable>
            <thead>
              <tr>
                <th>Colaborador</th>
                <th>Tipo</th>
                <th>Demissão</th>
                <th>Líquido</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {dados.map((r) => (
                <tr key={r.id}>
                  <td>{r.colaborador_nome || '—'}</td>
                  <td>{r.tipo_display || r.tipo || '—'}</td>
                  <td>{formatDate(r.data_demissao)}</td>
                  <td>{formatMoney(r.total_liquido ?? r.valor_liquido)}</td>
                  <td>
                    <StatusBadge tone={tone(r.status)}>
                      {r.status_display || r.status || '—'}
                    </StatusBadge>
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        ) : null}
      </Section>
    </section>
  )
}

export default RescisaoPage
